// src/components/onboarding/LanguageGate.tsx
//
// Top-level wrapper. Three states:
//
//   1. No explicit language choice:
//        → LanguageGateScreen (rotating welcome → picker)
//
//   2. Explicit choice but translations not ready yet
//      (Cloud Function generating, or doc not in cache yet):
//        → TranslationStandbyScreen
//
//   3. Translations ready (or English):
//        → render children
//
// The realtime status comes from useTranslationStatus, which
// listens to translations/{language_code} via onSnapshot. As
// new strings land in the doc, they're installed into the
// useT() dictionary on every snap so the UI doesn't have to
// wait for the WHOLE batch to finish before showing translated
// content.
//
// Skip path: if the user taps "Continue in English for now" on
// the standby screen, we set a session flag that lets them
// through immediately. Their language preference STAYS — the
// dictionary just keeps building in the background and Drop 3
// already shows progressively-translated UI as it lands.

import React, { useEffect, useState } from 'react';
import { useLanguagePreference } from '../../i18n/useLanguagePreference';
import { useTranslationStatus } from '../../i18n/useTranslationStatus';
import { LanguageGateScreen } from './LanguageGateScreen';
import { TranslationStandbyScreen } from './TranslationStandbyScreen';

interface LanguageGateProps {
  children: React.ReactNode;
}

export const LanguageGate: React.FC<LanguageGateProps> = ({ children }) => {
  const { language, hasExplicitChoice, setLanguage } = useLanguagePreference();
  const status = useTranslationStatus(language);
  // User chose to bypass the standby screen for THIS language.
  // Cleared whenever the language changes so a subsequent
  // language switch (e.g. via the flag in the topbar) shows
  // standby again.
  const [skipStandby, setSkipStandby] = useState(false);
  useEffect(() => {
    setSkipStandby(false);
  }, [language?.code]);

  if (!hasExplicitChoice) {
    return <LanguageGateScreen onSelect={setLanguage} />;
  }

  // English / null → status reports ready immediately, fall through.
  if (status.ready) {
    return <>{children}</>;
  }

  // User opted to skip the wait. Render the app; partial dict is
  // installed; missing strings render in English until the
  // listener catches up.
  if (skipStandby) {
    return <>{children}</>;
  }

  // Generating — show standby with progress.
  if (language) {
    return (
      <TranslationStandbyScreen
        language={language}
        completed={status.completed}
        total={status.total}
        onSkip={() => setSkipStandby(true)}
      />
    );
  }

  // Defensive — should be unreachable since hasExplicitChoice
  // requires a resolvable language. Render children rather than
  // strand the user.
  return <>{children}</>;
};
